import { z } from "zod";

/**
 * In-service changes — Extend, Upgrade, Add Service (0282/0283).
 * Rules: docs/IN_SERVICE_CHANGES_RULES.md. There are no add-ons (Luigi,
 * 2026-09-06), so these three are the whole list.
 *
 * The client names a VARIANT, never a price: the server prices every change
 * from its own catalog (SECURITY §5), and only offers what it would accept.
 */
export const inServiceChangeKindSchema = z.enum(["extension", "upgrade", "add_service"]);
export type InServiceChangeKind = z.infer<typeof inServiceChangeKindSchema>;

/** TEND-050's closed list. "other" requires a note. */
export const inServiceDeclineCodeSchema = z.enum([
  "equipment_unavailable",
  "client_mistake",
  "other",
]);
export type InServiceDeclineCode = z.infer<typeof inServiceDeclineCodeSchema>;

export const inServiceChangeOptionsSchema = z.object({ bookingId: z.string().uuid() }).strict();
export type InServiceChangeOptionsInput = z.infer<typeof inServiceChangeOptionsSchema>;

export const inServiceChangeRequestSchema = z
  .object({
    bookingId: z.string().uuid(),
    kind: inServiceChangeKindSchema,
    toVariantId: z.string().uuid(),
  })
  .strict();
export type InServiceChangeRequestInput = z.infer<typeof inServiceChangeRequestSchema>;

export const inServiceChangeDecisionSchema = z
  .object({
    changeId: z.string().uuid(),
    accept: z.boolean(),
    declineCode: inServiceDeclineCodeSchema.optional(),
    declineNote: z.string().trim().max(300).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.accept) return;
    if (!value.declineCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["declineCode"],
        message: "Choose a reason.",
      });
    } else if (value.declineCode === "other" && !value.declineNote) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["declineNote"],
        message: "Explain the reason.",
      });
    }
  });
export type InServiceChangeDecisionInput = z.infer<typeof inServiceChangeDecisionSchema>;

export const inServiceChangePaySchema = z.object({ changeId: z.string().uuid() }).strict();

/** C3 (Leon, 2026-10-04): an admin cancels a pending change, with a reason. */
export const inServiceChangeCancelSchema = z
  .object({ changeId: z.string().uuid(), reason: z.string().trim().min(1).max(300) })
  .strict();
export type InServiceChangeCancelInput = z.infer<typeof inServiceChangeCancelSchema>;
export type InServiceChangePayInput = z.infer<typeof inServiceChangePaySchema>;

/** One option on the change sheet — exactly what a request would accept. */
export const inServiceChangeOptionSchema = z.object({
  kind: inServiceChangeKindSchema,
  variantId: z.string().uuid(),
  serviceName: z.string(),
  durationMinutes: z.number().int(),
  minutesAdded: z.number().int(),
  amount: z.number(),
});
export type InServiceChangeOption = z.infer<typeof inServiceChangeOptionSchema>;

export const inServiceChangeStatusSchema = z.enum([
  "awaiting_therapist",
  "awaiting_payment",
  "applied",
  "declined",
  "expired",
  "cancelled",
  "payment_failed",
]);
export type InServiceChangeStatus = z.infer<typeof inServiceChangeStatusSchema>;

/** The open request, when there is one — it replaces the menu. */
export const inServiceOpenChangeSchema = z.object({
  id: z.string().uuid(),
  kind: inServiceChangeKindSchema,
  status: inServiceChangeStatusSchema,
  amount: z.number(),
  minutes: z.number().int(),
  serviceName: z.string(),
  durationMinutes: z.number().int(),
  pauseExpiresAt: z.string().nullable(),
});
export type InServiceOpenChange = z.infer<typeof inServiceOpenChangeSchema>;

/** C4: the most recent closed request, so the sheet can say how it ended. */
export const inServiceLastChangeSchema = z.object({
  id: z.string().uuid(),
  kind: inServiceChangeKindSchema,
  status: inServiceChangeStatusSchema,
  resolution: z.string().nullable(),
  serviceName: z.string(),
  resolvedAt: z.string().nullable(),
});
export type InServiceLastChange = z.infer<typeof inServiceLastChangeSchema>;

export const inServiceChangeMenuSchema = z.object({
  running: z.boolean(),
  open: inServiceOpenChangeSchema.nullable(),
  last: inServiceLastChangeSchema.nullable().optional(),
  options: z.array(inServiceChangeOptionSchema),
  paymentMethod: z.string(),
});
export type InServiceChangeMenu = z.infer<typeof inServiceChangeMenuSchema>;

/** What the therapist is asked to accept — no Client detail (data wall). */
export const therapistPendingChangeSchema = z.object({
  changeId: z.string().uuid(),
  bookingId: z.string().uuid(),
  kind: inServiceChangeKindSchema,
  serviceName: z.string(),
  durationMinutes: z.number().int(),
  minutesAdded: z.number().int(),
  requestedAt: z.string(),
  /**
   * 0369 — the server's own 5-minute deadline (C1). Null only for a request
   * written without one. The app counts down to it; it never computes one.
   */
  expiresAt: z.string().nullable(),
});
export type TherapistPendingChange = z.infer<typeof therapistPendingChangeSchema>;
